
import React from 'react';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import { CustomerData } from '@/hooks/admin/types';
import SubscriptionStatusDropdown from './subscription/SubscriptionStatusDropdown';
import { getPlanOption } from './subscription/PlanOptions';
import { Badge } from '@/components/ui/badge';

interface CustomerCardsProps {
  customers: CustomerData[];
  updateCustomerPlan: (subscriptionId: string, newPlan: string) => Promise<boolean>;
  createCustomerSubscription: (userId: string, plan: string) => Promise<boolean>;
  cancelCustomerSubscription: (userId: string, stripeSubscriptionId: string | null) => Promise<boolean>;
}

const CustomerCards: React.FC<CustomerCardsProps> = ({ 
  customers,
  updateCustomerPlan,
  createCustomerSubscription,
  cancelCustomerSubscription
}) => {
  return (
    <div className="space-y-4">
      {customers.map((customer) => {
        // Determine the display status
        const displayStatus = customer.status === 'canceled' ? 'Cancelled' : 
          (customer.plan ? customer.plan.charAt(0).toUpperCase() + customer.plan.slice(1).toLowerCase() : 'No Plan');
        
        // Get the plan option to use for color and styling
        const planOption = getPlanOption(displayStatus.toLowerCase());
        
        return (
          <div key={customer.id} className="border rounded-md p-4 space-y-2">
            <div className="flex justify-between items-center">
              <h3 className="font-medium">{customer.name}</h3>
              <Badge 
                variant="secondary" 
                className={`
                  ${planOption.mobileBackgroundColor} 
                  text-white 
                  border-none 
                  font-medium
                  shadow-sm
                  px-3
                  py-1
                  text-xs
                  tracking-wide
                  opacity-100
                  hover:opacity-90
                  transition-opacity
                  duration-200
                `}
              >
                {displayStatus}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{customer.email}</p>
            <Separator className="my-2" />
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <span className="text-muted-foreground">Tracks:</span>
              <span>{customer.tracksCount}</span>
              <span className="text-muted-foreground">Downloads:</span>
              <span>{customer.downloads}</span>
              <span className="text-muted-foreground">Page views:</span>
              <span>{customer.pageViews}</span>
              <span className="text-muted-foreground">Created:</span>
              <span>{format(new Date(customer.created_at), 'MMM d, yyyy')}</span>
            </div>
            <Separator className="my-2" />
            <div>
              <span className="text-sm text-muted-foreground mb-1 block">Subscription Status:</span>
              <SubscriptionStatusDropdown
                currentPlan={customer.status === 'canceled' ? 'canceled' : (customer.plan || 'pending')}
                customerId={customer.id}
                subscriptionId={customer.subscriptionId}
                stripeSubscriptionId={customer.stripeSubscriptionId}
                onUpdatePlan={updateCustomerPlan}
                onCreateSubscription={createCustomerSubscription}
                onCancelSubscription={cancelCustomerSubscription}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CustomerCards;
